import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../utils/apiConfig';

const AuthContext = createContext();

const BACKEND_URL = getBackendUrl();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// PREFETCH - Light version to improve perceived performance
const prefetchAllPageData = async (token) => {
  if (!token) return;
  
  console.log('🚀 Starting light prefetch...');
  
  try {
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache' 
    };
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    
    // Only prefetch the most critical endpoint with longer timeout
    const salesRes = await axios.get(
      `${BACKEND_URL}/api/sales?month=${month}&year=${year}`, 
      { headers, timeout: 30000 }
    ).catch(() => null);
    
    if (salesRes?.data) {
      localStorage.setItem('sales_cache_data', JSON.stringify(salesRes.data));
      console.log('✅ Sales prefetch complete');
    }
  } catch (e) {
    console.warn('Prefetch skipped:', e.message);
  }
};

/*
// FULL PREFETCH - DISABLED
const prefetchAllPageDataFull = async (token) => {
  if (!token) return;
  console.log('🚀 Prefetching all page data after login...');
  
  try {
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache' 
    };
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    
    // Fetch all data in parallel
    const results = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/manager-dashboard/combined?month=${month}&year=${year}`, { headers, timeout: 10000 }),
      axios.get(`${BACKEND_URL}/api/sales-dashboard/combined?month=${month}&year=${year}&view_mode=monthly`, { headers, timeout: 10000 }),
      axios.get(`${BACKEND_URL}/api/leads`, { headers, timeout: 10000 })
    ]);
    
    const [dashboardRes, salesRes, leadsRes] = results;
    
    // Cache Dashboard data
    if (dashboardRes.status === 'fulfilled' && dashboardRes.value?.data) {
      const d = dashboardRes.value.data;
      // Dynamic teams: cache per team
      const teamNames = d.team_names || Object.keys(d.teams || {});
      teamNames.forEach(tn => {
        const td = d.teams?.[tn] || d[tn] || {};
        localStorage.setItem(`manager_cache_team_${tn}_data`, JSON.stringify({ sales: td.sales || [], objectives: td.objectives || {} }));
        localStorage.setItem(`manager_cache_team_${tn}_advisors`, JSON.stringify(td.advisors || []));
      });
      // Backward compat
      if (d.eam) localStorage.setItem('manager_cache_eamData', JSON.stringify({ sales: d.eam?.sales || [], objectives: d.eam?.objectives || {} }));
      if (d.nitin) localStorage.setItem('manager_cache_nitinData', JSON.stringify({ sales: d.nitin?.sales || [], objectives: d.nitin?.objectives || {} }));
      if (d.eam) localStorage.setItem('manager_cache_eamAdvisors', JSON.stringify(d.eam?.advisors || []));
      if (d.nitin) localStorage.setItem('manager_cache_nitinAdvisors', JSON.stringify(d.nitin?.advisors || []));
      localStorage.setItem('manager_cache_alerts', JSON.stringify(d.alerts || {}));
      localStorage.setItem('manager_cache_inventoryStats', JSON.stringify(d.inventory || {}));
      localStorage.setItem('manager_cache_marketingStats', JSON.stringify(d.marketing || {}));
      console.log('✅ Dashboard cache populated');
    }
    
    // Cache Sales page data
    if (salesRes.status === 'fulfilled' && salesRes.value?.data) {
      const s = salesRes.value.data;
      // Dynamic teams: cache per team
      const teamNames = s.team_names || Object.keys(s.teams || {});
      teamNames.forEach(tn => {
        const td = s.teams?.[tn] || s[tn] || {};
        localStorage.setItem(`sales_cache_team_${tn}_sales`, JSON.stringify(td.sales || []));
        localStorage.setItem(`sales_cache_team_${tn}_advisors`, JSON.stringify(td.advisors || []));
        localStorage.setItem(`sales_cache_team_${tn}_objectives`, JSON.stringify(td.objectives || {}));
      });
      // Backward compat
      if (s.eam) localStorage.setItem('sales_cache_eamSales', JSON.stringify(s.eam?.sales || []));
      if (s.nitin) localStorage.setItem('sales_cache_nitinSales', JSON.stringify(s.nitin?.sales || []));
      if (s.eam) localStorage.setItem('sales_cache_eamAdvisors', JSON.stringify(s.eam?.advisors || []));
      if (s.nitin) localStorage.setItem('sales_cache_nitinAdvisors', JSON.stringify(s.nitin?.advisors || []));
      if (s.eam) localStorage.setItem('sales_cache_eamObjectives', JSON.stringify(s.eam?.objectives || {}));
      if (s.nitin) localStorage.setItem('sales_cache_nitinObjectives', JSON.stringify(s.nitin?.objectives || {}));
      localStorage.setItem('sales_cache_banks', JSON.stringify(s.banks || []));
      localStorage.setItem('sales_cache_ytdStats', JSON.stringify(s.ytd || null));
      console.log('✅ Sales cache populated');
    }
    
    // Cache CRM/Marketing page data
    if (leadsRes.status === 'fulfilled' && leadsRes.value?.data) {
      localStorage.setItem('crm_cache_leads', JSON.stringify(leadsRes.value.data));
      console.log('✅ CRM cache populated');
    }
    
    console.log('✅ All page data prefetch complete');
  } catch (e) {
    console.warn('Prefetch error:', e);
  }
};
*/

// Helper to validate token
const isValidToken = (token) => {
  return token && 
         typeof token === 'string' && 
         token.trim() !== '' && 
         token !== 'undefined' && 
         token !== 'null';
};

// Helper to set up axios with token
const setupAxiosAuth = (token) => {
  if (isValidToken(token)) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
  // Cache-busting headers
  axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  axios.defaults.headers.common['Pragma'] = 'no-cache';
  axios.defaults.headers.common['Expires'] = '0';
};

// Configure axios defaults for better stability
axios.defaults.timeout = 30000; // 30 second timeout

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously
  const [user, setUser] = useState(() => {
    const cachedUser = localStorage.getItem('user');
    return cachedUser ? JSON.parse(cachedUser) : null;
  });
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    const saved = localStorage.getItem('impersonatedUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [isReady, setIsReady] = useState(false);
  
  // Ref to track if initial auth check is done
  const authCheckDone = useRef(false);
  
  // Set up axios immediately with stored token
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setupAxiosAuth(storedToken);
    }
  }, []);

  // Auto-detect correct backend URL based on current domain
  const getBackendUrl = useCallback(() => {
    // For custom domain deployments, use the current origin
    // For Emergent preview/production, REACT_APP_BACKEND_URL is set
    // This handles all deployment scenarios: custom domains, Emergent preview, and Emergent production
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  }, []);
  
  const backendUrl = getBackendUrl();

  // Set up axios interceptor once on mount
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      // Always get fresh token from localStorage for each request
      const currentToken = localStorage.getItem('token');
      if (currentToken && !config.url.includes('/auth/login')) {
        config.headers['Authorization'] = `Bearer ${currentToken}`;
      }
      
      // Add timestamp to GET requests to prevent caching (except auth endpoints)
      if (config.method === 'get' && !config.url.includes('/auth/')) {
        const separator = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}_t=${Date.now()}`;
      }
      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // Check if user is logged in on mount - only once
  useEffect(() => {
    const checkAuth = async () => {
      if (authCheckDone.current) return;
      authCheckDone.current = true;
      
      const storedToken = localStorage.getItem('token');
      const cachedUser = localStorage.getItem('user');
      
      // Use the global isValidToken function
      if (!isValidToken(storedToken)) {
        // Clear invalid token
        if (storedToken) {
          console.warn('[Auth] Invalid token found, clearing');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
        setLoading(false);
        return;
      }
      
      if (storedToken) {
        setupAxiosAuth(storedToken);
        
        // If we have cached user data, use it immediately
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser(userData);
            setToken(storedToken);
            setIsReady(true);
            console.log('✅ Auth restored from cache');
            
            // PREFETCH all page data when app loads with existing session
            prefetchAllPageData(storedToken);
          } catch (e) {
            console.error('Failed to parse cached user');
          }
        }
        
        // Verify token in background (don't block UI)
        try {
          const response = await axios.get(`${backendUrl}/api/auth/me`);
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          setIsReady(true);
          console.log('✅ Auth verified with server');
        } catch (error) {
          console.error('Auth check failed:', error);
          if (error.response?.status === 401) {
            // Token is invalid - clear everything
            console.log('Token expired or invalid, clearing auth');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            setIsReady(false);
          }
          // For network errors, keep using cached data
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [backendUrl]);

  // Update isReady when token and user change
  useEffect(() => {
    if (token && user) {
      setIsReady(true);
    } else if (!token) {
      setIsReady(false);
    }
  }, [token, user]);

  const login = async (email, password) => {
    // Clear any existing auth state first
    delete axios.defaults.headers.common['Authorization'];
    
    try {
      console.log('🔐 Attempting login...');
      
      const response = await axios.post(`${backendUrl}/api/auth/login`, {
        email,
        password
      }, {
        timeout: 20000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const { access_token, user: userData } = response.data;
      
      // Store in localStorage FIRST
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Dispatch custom event for other contexts to know auth changed
      window.dispatchEvent(new Event('authStateChanged'));
      
      // Set up axios with new token IMMEDIATELY
      setupAxiosAuth(access_token);
      
      // Update state
      setToken(access_token);
      setUser(userData);
      setIsReady(true);
      
      console.log('✅ Login successful, auth ready');
      
      // PREFETCH all page data immediately after login for instant navigation
      prefetchAllPageData(access_token);
      
      return { success: true };
      
    } catch (error) {
      console.error('Login failed:', error);
      
      // Clear any partial state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setIsReady(false);
      
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Erreur de connexion. Veuillez réessayer.';
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = useCallback(() => {
    console.log('🔓 Logging out...');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonatedUser');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setImpersonatedUser(null);
    setIsReady(false);
  }, []);

  const impersonateUser = useCallback((userToImpersonate) => {
    setImpersonatedUser(userToImpersonate);
    localStorage.setItem('impersonatedUser', JSON.stringify(userToImpersonate));
  }, []);

  const exitImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    localStorage.removeItem('impersonatedUser');
  }, []);

  // Return impersonated user if active, otherwise return real user
  const effectiveUser = impersonatedUser || user;

  const value = {
    user: effectiveUser,
    realUser: user,
    impersonatedUser,
    impersonateUser,
    exitImpersonation,
    isImpersonating: !!impersonatedUser,
    login,
    logout,
    loading,
    token,
    isAuthenticated: !!user && !!token,
    isReady: isReady && !!user && !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
